// Web-compatible WebRTC Manager for web environments
// This file provides browser-native WebRTC APIs instead of react-native-webrtc

export class WebRTCManager {
	private static instance: WebRTCManager;
	private peerConnection: RTCPeerConnection | null = null;
	private localStream: MediaStream | null = null;
	private remoteStream: MediaStream | null = null;
	private socketManager: any;
	private callId: string | null = null;

	private constructor() {
		// Initialize socket manager
		const SocketManager = require('./socketManager').default;
		this.socketManager = SocketManager.getInstance();
		this.setupSocketListeners();
	}

	static getInstance(): WebRTCManager {
		if (!WebRTCManager.instance) {
			WebRTCManager.instance = new WebRTCManager();
		}
		return WebRTCManager.instance;
	}

	private setupSocketListeners(): void {
		// Setup socket listeners for WebRTC events
		this.socketManager.onSDPOffer(this.handleSDPOffer.bind(this));
		this.socketManager.onSDPAnswer(this.handleSDPAnswer.bind(this));
		this.socketManager.onICECandidate(this.handleICECandidate.bind(this));
	}

	async initializePeerConnection(callId: string): Promise<RTCPeerConnection> {
		this.callId = callId;
		this.peerConnection = new RTCPeerConnection({
			iceServers: [
				{ urls: 'stun:stun.l.google.com:19302' },
				{ urls: 'stun:stun1.l.google.com:19302' },
			],
		});

		this.peerConnection.addEventListener('icecandidate', (event) => {
			if (event.candidate && this.callId) {
				this.socketManager.sendICECandidate({
					callId: this.callId,
					candidate: event.candidate.candidate,
					sdpMLineIndex: event.candidate.sdpMLineIndex,
					sdpMid: event.candidate.sdpMid,
				});
			}
		});

		this.peerConnection.addEventListener('track', (event) => {
			this.remoteStream = event.streams[0];
			console.log('Remote stream received:', event.streams[0]);
		});

		return this.peerConnection;
	}

	async getLocalMedia(video: boolean = true): Promise<MediaStream> {
		try {
			const stream = await navigator.mediaDevices.getUserMedia({
				video: video
					? {
							width: { min: 320, ideal: 1280, max: 1920 },
							height: { min: 240, ideal: 720, max: 1080 },
							frameRate: { min: 15, ideal: 30, max: 30 },
						}
					: false,
				audio: true,
			});

			this.localStream = stream;
			return stream;
		} catch (error) {
			console.error('Error accessing media devices:', error);
			throw error;
		}
	}

	async createOffer(): Promise<RTCSessionDescription> {
		if (!this.peerConnection) {
			throw new Error('Peer connection not initialized');
		}

		const offer = await this.peerConnection.createOffer({});
		await this.peerConnection.setLocalDescription(offer);
		return offer;
	}

	async createAnswer(offer: RTCSessionDescription): Promise<RTCSessionDescription> {
		if (!this.peerConnection) {
			throw new Error('Peer connection not initialized');
		}

		await this.peerConnection.setRemoteDescription(offer);
		const answer = await this.peerConnection.createAnswer({});
		await this.peerConnection.setLocalDescription(answer);
		return answer;
	}

	async handleSDPOffer(data: any): Promise<void> {
		if (!this.peerConnection) return;

		try {
			await this.peerConnection.setRemoteDescription(
				new RTCSessionDescription(data)
			);
			const answer = await this.createAnswer(
				new RTCSessionDescription(data)
			);

			this.socketManager.sendSDPAnswer({
				callId: this.callId,
				sdp: answer.sdp,
				type: answer.type,
			});
		} catch (error) {
			console.error('Error handling SDP offer:', error);
		}
	}

	async handleSDPAnswer(data: any): Promise<void> {
		if (!this.peerConnection) return;

		try {
			await this.peerConnection.setRemoteDescription(
				new RTCSessionDescription(data)
			);
		} catch (error) {
			console.error('Error handling SDP answer:', error);
		}
	}

	async handleICECandidate(data: any): Promise<void> {
		if (!this.peerConnection) return;

		try {
			await this.peerConnection.addIceCandidate(
				new RTCIceCandidate(data)
			);
		} catch (error) {
			console.error('Error handling ICE candidate:', error);
		}
	}

	addTracksToPeerConnection(): void {
		if (!this.peerConnection || !this.localStream) return;

		this.localStream.getTracks().forEach((track) => {
			this.peerConnection!.addTrack(track, this.localStream!);
		});
	}

	toggleVideo(enabled: boolean): void {
		if (this.localStream) {
			const videoTracks = this.localStream.getVideoTracks();
			videoTracks.forEach((track) => {
				track.enabled = enabled;
			});
		}
	}

	toggleAudio(enabled: boolean): void {
		if (this.localStream) {
			const audioTracks = this.localStream.getAudioTracks();
			audioTracks.forEach((track) => {
				track.enabled = enabled;
			});
		}
	}

	getLocalStream(): MediaStream | null {
		return this.localStream;
	}

	getRemoteStream(): MediaStream | null {
		return this.remoteStream;
	}

	cleanup(): void {
		if (this.localStream) {
			const tracks = this.localStream.getTracks();
			if (tracks) {
				tracks.forEach((track) => track.stop());
			}
			this.localStream = null;
		}

		if (this.remoteStream) {
			this.remoteStream = null;
		}

		if (this.peerConnection) {
			this.peerConnection.close();
			this.peerConnection = null;
		}

		this.callId = null;
	}

	// Media permission helpers
	async requestMediaPermissions(): Promise<boolean> {
		try {
			await this.getLocalMedia(true);
			return true;
		} catch (error) {
			console.error('Media permissions not granted:', error);
			return false;
		}
	}

	async checkMediaPermissions(): Promise<boolean> {
		try {
			await this.getLocalMedia(true);
			return true;
		} catch (error) {
			console.error('Media permissions not available:', error);
			return false;
		}
	}
}

export default WebRTCManager;
